import { useEffect, useState } from 'react';
import { Card } from '../types';
import RarityBadge from './RarityBadge';

interface CardFlipProps {
  card: Card;
  index: number;
  isLast: boolean;
  revealDelay: number; // ms
  onRevealed?: () => void;
}

/** Card back — Pokémon-style blue back */
function CardBack() {
  return (
    <div className="w-full h-full rounded-xl bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center border-2 border-blue-500/30">
      <div className="w-14 h-14 rounded-full bg-blue-600/50 border-2 border-blue-400/30 flex items-center justify-center text-2xl">
        🎴
      </div>
    </div>
  );
}

export default function CardFlip({ card, index, isLast, revealDelay, onRevealed }: CardFlipProps) {
  const [flipped, setFlipped] = useState(false);
  const [appeared, setAppeared] = useState(false);

  useEffect(() => {
    // Stagger appearance then flip
    const appearTimer = setTimeout(() => setAppeared(true), revealDelay);
    const flipTimer = setTimeout(() => {
      setFlipped(true);
      onRevealed?.();
    }, revealDelay + 300);

    return () => {
      clearTimeout(appearTimer);
      clearTimeout(flipTimer);
    };
  }, [revealDelay, onRevealed]);

  const isUltraRare = card.rarity === 'ultra-rare';

  return (
    <div
      className={`
        card-container relative
        ${isLast ? 'col-span-2 mx-auto w-full max-w-[180px] aspect-[2.5/3.5]' : 'aspect-[2.5/3.5]'}
        ${appeared ? 'card-appear' : 'opacity-0'}
        ${isLast && flipped && isUltraRare ? 'ultra-rare-glow' : ''}
        ${isLast && flipped ? 'scale-110 z-10' : ''}
        transition-transform duration-300
      `}
    >
      <div className={`card-inner ${flipped ? 'flipped' : ''}`}>
        {/* Front (shown when not flipped) */}
        <div className="card-front">
          <CardBack />
        </div>

        {/* Back (shown when flipped — this is the actual card) */}
        <div className="card-back">
          <div className="relative w-full h-full rounded-xl overflow-hidden border border-white/10">
            <img
              src={card.image_url}
              alt={card.pokemon_name}
              className="w-full h-full object-cover"
              loading="lazy"
            />

            {/* Holographic shimmer for rare+ */}
            {(card.rarity === 'rare' || card.rarity === 'ultra-rare') && (
              <div className="absolute inset-0 holo-shimmer pointer-events-none" />
            )}

            {/* Rarity badge overlay */}
            <div className="absolute bottom-1 left-1">
              <RarityBadge rarity={card.rarity} />
            </div>

            {/* Card index for non-last */}
            {!isLast && (
              <div className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center text-xs text-gray-300">
                {index + 1}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* BIG REVEAL label for last card */}
      {isLast && flipped && (
        <div className="absolute -top-6 left-0 right-0 text-center">
          <span className="text-accent-gold font-bold text-sm uppercase tracking-widest animate-pulse">
            ✨ Rare Pull!
          </span>
        </div>
      )}
    </div>
  );
}
